import React from "react"
import { motion } from "framer-motion"
import { useTheme } from "../contexts/ThemeContext"

// ── data ─────────────────────────────────────────────────────────────────────

const reviews = [
  {
    initials: "SJ", avatarBg: "#1D3047",
    name: "Sarah Johnson", sub: "Daily Commuter · Boston, MA",
    rating: 5, since: "Jan 2024", stat2Label: "SAVED", stat2Val: "38 min / week",
    featured: false, verified: true, trips: null,
    text: (
      <>
        SaferSavvy has transformed my daily commute. The{" "}
        <strong style={{ color: "#1D3047" }}>AI predictions are incredibly accurate</strong>
        , and I love how it suggests alternate routes during traffic jams.
      </>
    ),
  },
  {
    initials: "MC", avatarBg: "#2D5F9E",
    name: "Michael Chen", sub: "Business Traveler · San Francisco, CA",
    rating: 5, since: "Aug 2023", stat2Label: "TRIPS", stat2Val: "412 logged",
    featured: true, verified: false, trips: true,
    text: (
      <>
        As someone who travels frequently for work, this app has been a{" "}
        <strong>game-changer</strong>. It consistently finds the fastest routes and
        the real-time updates are invaluable on tight schedules.
      </>
    ),
  },
  {
    initials: "ER", avatarBg: "#1D3047",
    name: "Emily Rodriguez", sub: "Student · Austin, TX",
    rating: 4, since: "Sep 2024", stat2Label: "SAVED", stat2Val: "100 min / week",
    featured: false, verified: true, trips: null,
    text: (
      <>
        The multi-modal planning feature is perfect for students like me who use
        different types of public transport.{" "}
        <strong style={{ color: "#1D3047" }}>I save at least 20 minutes</strong>{" "}
        on my commute every single day.
      </>
    ),
  },
]

const stats = [
  {
    cat: "ACTIVE USERS",
    val: "10", unit: "M+", valColor: "#1D3047",
    label: "Riders worldwide", sub: "+24% YoY growth",
    bars: [3, 4, 5, 6, 7, 8, 9, 10, 8, 6, 9, 10],
    activeCount: 9, activeColor: "#2D5F9E",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
  {
    cat: "CITIES COVERED",
    val: "30", unit: "+", valColor: "#1D3047",
    label: "Across 14 countries", sub: "8 new this quarter",
    bars: [3, 5, 4, 7, 6, 8, 9, 8, 10, 7, 9, 10],
    activeCount: 9, activeColor: "#2D5F9E",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
      </svg>
    ),
  },
  {
    cat: "USER SATISFACTION",
    val: "98", unit: "%", valColor: "#1D3047",
    label: "Would recommend", sub: "Surveyed Q1 2026",
    bars: [5, 6, 7, 8, 7, 9, 8, 10, 9, 8, 10, 9],
    activeCount: 10, activeColor: "#2D5F9E",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
      </svg>
    ),
  },
  {
    cat: "TIME SAVED",
    val: "42", unit: " min", valColor: "#E3B02E",
    label: "Avg per rider, weekly", sub: "Up from 31 min last year",
    bars: [3, 4, 5, 6, 5, 7, 6, 8, 7, 9, 10, 9],
    activeCount: 10, activeColor: "#E3B02E",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
      </svg>
    ),
  },
]

// ── Stars ─────────────────────────────────────────────────────────────────────
const Stars = ({ rating, light }: { rating: number; light?: boolean }) => (
  <div style={{ display: "flex", gap: 2 }}>
    {[1, 2, 3, 4, 5].map((s) => (
      <svg key={s} width="14" height="14" viewBox="0 0 24 24"
        fill={s <= rating ? "#E3B02E" : (light ? "rgba(255,255,255,0.25)" : "#e2e8f0")}
        stroke="none">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ))}
    <span style={{ fontSize: 12, fontWeight: 700, color: light ? "rgba(255,255,255,0.8)" : "#64748b", marginLeft: 4 }}>
      {rating}.0
    </span>
  </div>
)

// ── component ─────────────────────────────────────────────────────────────────
const Testimonials: React.FC = () => {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  return (
    <section
      id="testimonials"
      className="py-16 md:py-24 px-4 sm:px-6 lg:px-16 xl:px-24 overflow-hidden"
      style={{
        background: isDark
          ? "linear-gradient(160deg,#13161e 0%,#181c26 45%,#1b2230 100%)"
          : "linear-gradient(160deg,#EEF2FF 0%,#F5F8FF 45%,#FFFFFF 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto">

        {/* ── header ── */}
        <motion.div
          className="text-center mb-8 md:mb-10"
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2
            className="font-black tracking-tight mb-3"
            style={{ fontSize: "clamp(28px, 4vw, 48px)", color: "#1D3047" }}
          >
            <span style={{ color: "#2D5F9E" }}>What our </span>
            <span style={{ color: "#E3B02E" }}>users say.</span>
          </h2>
          <p className="text-sm sm:text-[15px] max-w-xl mx-auto" style={{ color: "#64748b" }}>
            Don't just take our word for it. Here's what people who use SaferSavvy every day
            have to say about the experience.
          </p>

          {/* rating pill */}
          <motion.div
            className="inline-flex flex-wrap items-center gap-3 sm:gap-4 mt-5 bg-white rounded-full border border-slate-200 px-5 py-2.5"
            style={{ boxShadow: "0 2px 12px rgba(29,48,71,0.07)" }}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {[1,2,3,4,5].map(s => (
                <svg key={s} width="14" height="14" viewBox="0 0 24 24" fill="#E3B02E" stroke="none">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              ))}
              <span className="font-bold text-sm ml-1" style={{ color: "#1D3047" }}>4.8 / 5</span>
              <span className="text-slate-300 mx-1">·</span>
              <span className="text-[12px]" style={{ color: "#64748b" }}>12,400+ verified reviews</span>
            </div>
            <div className="h-4 w-px bg-slate-200 hidden sm:block" />
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="text-[12px] font-semibold" style={{ color: "#2D5F9E" }}>App Store</span>
              <span className="text-[11px]" style={{ color: "#94a3b8" }}>· Editor's Choice</span>
            </div>
          </motion.div>
        </motion.div>

        {/* ── 3 review cards ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 mb-5">
          {reviews.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              viewport={{ once: true }}
              className="flex flex-col rounded-2xl border"
              style={{
                padding: "20px 22px",
                background: r.featured ? "#2D5F9E" : "white",
                borderColor: r.featured ? "#2D5F9E" : "#e2e8f0",
                boxShadow: r.featured
                  ? "0 16px 48px rgba(45,95,158,0.25), 0 2px 8px rgba(0,0,0,0.06)"
                  : "0 4px 20px rgba(29,48,71,0.06), 0 1px 4px rgba(0,0,0,0.03)",
                transform: r.featured ? "scale(1.02)" : "scale(1)",
              }}
            >
              {/* avatar + quote */}
              <div className="flex items-start justify-between mb-4">
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
                    background: r.featured ? "rgba(255,255,255,0.2)" : r.avatarBg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: r.featured ? "2px solid rgba(255,255,255,0.5)" : "none",
                  }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: "white", letterSpacing: "0.03em" }}>
                      {r.initials}
                    </span>
                  </div>
                  <div>
                    <p style={{ fontSize: 13.5, fontWeight: 700, color: r.featured ? "white" : "#1D3047", lineHeight: 1.2 }}>
                      {r.name}
                    </p>
                    <p style={{ fontSize: 10.5, color: r.featured ? "rgba(255,255,255,0.65)" : "#94a3b8", marginTop: 2 }}>
                      {r.sub}
                    </p>
                  </div>
                </div>
                <div style={{
                  width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                  background: r.featured ? "rgba(255,255,255,0.15)" : "#FFF8E7",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill={r.featured ? "rgba(255,255,255,0.7)" : "#E3B02E"}>
                    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
                    <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
                  </svg>
                </div>
              </div>

              {/* stars */}
              <div className="mb-3">
                <Stars rating={r.rating} light={r.featured} />
              </div>

              {/* text */}
              <p className="text-[13px] leading-relaxed flex-1 mb-5"
                style={{ color: r.featured ? "rgba(255,255,255,0.88)" : "#475569" }}>
                {r.text}
              </p>

              {/* footer */}
              <div
                className="flex items-center justify-between pt-3"
                style={{ borderTop: `1px solid ${r.featured ? "rgba(255,255,255,0.15)" : "#f1f5f9"}` }}
              >
                <div style={{ display: "flex", gap: 20 }}>
                  <div>
                    <p style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: r.featured ? "rgba(255,255,255,0.5)" : "#94a3b8" }}>
                      USING SINCE
                    </p>
                    <p style={{ fontSize: 12.5, fontWeight: 700, color: r.featured ? "white" : "#1D3047" }}>
                      {r.since}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: r.featured ? "rgba(255,255,255,0.5)" : "#94a3b8" }}>
                      {r.stat2Label}
                    </p>
                    <p style={{ fontSize: 12.5, fontWeight: 700, color: r.featured ? "white" : "#1D3047" }}>
                      {r.stat2Val}
                    </p>
                  </div>
                </div>
                {r.featured ? (
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    background: "#E3B02E", color: "#1D3047",
                    fontSize: 10, fontWeight: 800, borderRadius: 20,
                    padding: "4px 10px", whiteSpace: "nowrap",
                  }}>
                    ★ Featured
                  </span>
                ) : (
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    border: "1px solid #e2e8f0", color: "#64748b",
                    fontSize: 10, fontWeight: 600, borderRadius: 20,
                    padding: "3px 9px", whiteSpace: "nowrap",
                  }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#2D5F9E" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Verified
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* ── 4 stat cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.09 }}
              viewport={{ once: true }}
              className="rounded-2xl bg-white border border-slate-200/80"
              style={{
                padding: "18px 20px",
                boxShadow: "0 4px 20px rgba(29,48,71,0.06), 0 1px 4px rgba(0,0,0,0.03)",
              }}
            >
              {/* cat + icon */}
              <div className="flex items-center justify-between mb-3">
                <span style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                  {s.cat}
                </span>
                <div style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: "#F1F5FF", color: "#2D5F9E",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {s.icon}
                </div>
              </div>

              {/* value */}
              <div style={{ display: "flex", alignItems: "baseline", gap: 2, marginBottom: 2 }}>
                <span style={{ fontSize: 36, fontWeight: 900, lineHeight: 1, color: s.valColor, letterSpacing: "-0.02em" }}>
                  {s.val}
                </span>
                <span style={{ fontSize: 18, fontWeight: 800, color: s.valColor }}>{s.unit}</span>
              </div>

              <p style={{ fontSize: 12, fontWeight: 600, color: "#2D5F9E", marginBottom: 2 }}>{s.label}</p>
              <p style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10.5, color: "#64748b", marginBottom: 12 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", display: "inline-block", flexShrink: 0 }} />
                {s.sub}
              </p>

              {/* mini bar chart */}
              <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 20 }}>
                {s.bars.map((h, bi) => (
                  <div
                    key={bi}
                    style={{
                      flex: 1, borderRadius: 2,
                      height: `${(h / 10) * 100}%`,
                      background: bi >= s.bars.length - s.activeCount
                        ? s.activeColor
                        : "#e2e8f0",
                    }}
                  />
                ))}
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  )
}

export default Testimonials
